import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import s from '../../pages/PedidosOrcamentos.module.css';
import InputDataFiltro from '../common/InputDataFiltro';

const fmtMoeda = v => v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDT    = d => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmtCnpj  = c => c && c.length === 14
  ? c.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : (c || '—');

const SITUACAO = { 1: null, 2: 'Denegada', 3: 'Cancelada' };

const isoHoje    = () => new Date().toLocaleDateString('en-CA');
const isoIniMes  = () => isoHoje().slice(0, 8) + '01';

// A SEFAZ exige 1h entre consultas (cStat 656); o ciclo automático roda a cada 70 min
const MIN_INTERVALO_MANUAL_MIN = 65;

export default function ModalDfe({ onImportar, onClose }) {
  const [docs, setDocs]         = useState([]);
  const [controle, setControle] = useState(null);
  const [busca, setBusca]       = useState('');
  const [zipDe, setZipDe]       = useState(isoIniMes());
  const [zipAte, setZipAte]     = useState(isoHoje());
  // null = janela padrão (últimos N dias); { de, ate } = período filtrado
  const [periodo, setPeriodo]   = useState(null);
  const [janelaDias, setJanelaDias] = useState(null);
  const [baixandoZip, setBaixandoZip] = useState(false);
  const [chaveConsulta, setChaveConsulta]     = useState('');
  const [consultandoChave, setConsultandoChave] = useState(false);
  const [carregando, setCarregando]     = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [ocupadoId, setOcupadoId] = useState(null);
  const [erro, setErro]         = useState('');
  const [aviso, setAviso]       = useState('');
  const [agora, setAgora]       = useState(Date.now());

  // Relógio para reavaliar o bloqueio dos botões de consulta
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const minDesdeConsulta = controle?.ultima_consulta
    ? (agora - new Date(controle.ultima_consulta).getTime()) / 60000
    : Infinity;
  const minutosFaltando = minDesdeConsulta < MIN_INTERVALO_MANUAL_MIN
    ? Math.ceil(MIN_INTERVALO_MANUAL_MIN - minDesdeConsulta)
    : 0;
  const consultaBloqueada = minutosFaltando > 0;

  const carregar = useCallback(async (b = busca, per = periodo) => {
    setCarregando(true);
    try {
      const { data } = await api.get('/dfe/documentos',
        { params: { busca: b || undefined, de: per?.de, ate: per?.ate } });
      setDocs(data.documentos || []);
      setControle(data.controle || null);
      setJanelaDias(data.janela_dias || null);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao carregar documentos do DF-e.');
    } finally { setCarregando(false); }
  }, [busca, periodo]);

  const filtrarPeriodo = () => {
    if (!zipDe || !zipAte) { setErro('Informe as duas datas do período.'); return; }
    if (zipDe > zipAte) { setErro('A data inicial é maior que a data final.'); return; }
    setErro('');
    const per = { de: zipDe, ate: zipAte };
    setPeriodo(per);
    carregar(busca, per);
  };

  const limparPeriodo = () => {
    setPeriodo(null);
    carregar(busca, null);
  };

  const fmtD = iso => iso.split('-').reverse().join('/');

  useEffect(() => { carregar(''); }, []); // eslint-disable-line

  const sincronizar = async (reiniciar = false) => {
    setErro(''); setAviso(''); setSincronizando(true);
    try {
      const { data } = await api.post('/dfe/sincronizar', reiniciar ? { reiniciar: true } : {});
      setAviso(data.aviso
        ? data.aviso
        : `Sincronizado com a SEFAZ (${data.cStat} — ${data.xMotivo || ''}): ${data.novos} documento(s) novo(s)` +
          (data.ciencias ? `, ${data.ciencias} ciência(s) automática(s)` : '') +
          (data.xmls_baixados ? `, ${data.xmls_baixados} XML(s) completo(s) baixado(s)` : '') + '.');
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || err.message || 'Erro ao sincronizar com a SEFAZ.');
    } finally { setSincronizando(false); }
  };

  const reconsultarTudo = () => {
    if (!window.confirm(
      'Reconsultar TUDO desde o início?\n\n' +
      'Zera o controle de NSU e busca novamente todos os documentos que a SEFAZ ' +
      'mantém disponíveis (últimos 90 dias). Documentos já baixados não são duplicados.\n\n' +
      'Atenção: a SEFAZ limita consultas repetidas — se aparecer o erro 656, aguarde 1 hora.'
    )) return;
    sincronizar(true);
  };

  const naoRealizada = async (doc) => {
    const just = window.prompt(
      'Operação NÃO realizada — a mercadoria não foi recebida/negócio não se concretizou.\n\n' +
      'Informe a justificativa (mínimo 15 caracteres):'
    );
    if (just === null) return;
    if (just.trim().length < 15) { setErro('Justificativa deve ter no mínimo 15 caracteres.'); return; }
    setOcupadoId(doc.id); setErro('');
    try {
      await api.post(`/dfe/documentos/${doc.id}/manifestar`,
        { tpEvento: 210240, justificativa: just.trim() });
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.xMotivo || err.response?.data?.erro || 'Erro ao manifestar.');
    } finally { setOcupadoId(null); }
  };

  const manifestar = async (doc) => {
    if (!window.confirm(
      `Manifestar CIÊNCIA DA OPERAÇÃO para a nota de ${doc.nome_emitente || fmtCnpj(doc.cnpj_emitente)}?\n\n` +
      `A ciência informa à SEFAZ que você tomou conhecimento da nota — o sistema já tenta baixar o XML completo na sequência.`
    )) return;
    setErro(''); setAviso(''); setOcupadoId(doc.id);
    try {
      const { data } = await api.post(`/dfe/documentos/${doc.id}/manifestar`);
      setAviso(data.aviso || `${data.cStat} — ${data.xMotivo}`);
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.xMotivo || err.response?.data?.erro || 'Erro ao manifestar.');
    } finally { setOcupadoId(null); }
  };

  const baixarZip = async () => {
    setErro(''); setAviso(''); setBaixandoZip(true);
    try {
      const resp = await api.get('/dfe/xmls-zip',
        { params: { de: zipDe, ate: zipAte }, responseType: 'blob' });
      const nome = (resp.headers['content-disposition'] || '').match(/filename="(.+)"/)?.[1]
        || `xmls-compras_${zipDe}_a_${zipAte}.zip`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = nome; a.click();
      URL.revokeObjectURL(url);
      setAviso(`Arquivo ${nome} baixado.`);
    } catch (err) {
      let msg = 'Erro ao baixar os XMLs.';
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).erro || msg; } catch (_) {}
      } else msg = err.response?.data?.erro || msg;
      setErro(msg);
    } finally { setBaixandoZip(false); }
  };

  const consultarChave = async () => {
    const ch = chaveConsulta.replace(/\D/g, '');
    if (ch.length !== 44) { setErro('Informe a chave de acesso completa (44 dígitos).'); return; }
    setErro(''); setAviso(''); setConsultandoChave(true);
    try {
      const { data } = await api.post('/dfe/consultar-chave', { chave: ch });
      setAviso(data.mensagem);
      if (['baixada', 'resumo', 'ja_baixada'].includes(data.resultado)) {
        setChaveConsulta('');
        await carregar();
      }
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao consultar a chave.');
    } finally { setConsultandoChave(false); }
  };

  const importar = async (doc) => {
    setErro(''); setOcupadoId(doc.id);
    try {
      const { data } = await api.get(`/dfe/documentos/${doc.id}/xml`);
      await onImportar(data.xml);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao obter o XML.');
    } finally { setOcupadoId(null); }
  };

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ width: 'min(1180px, 96vw)', maxWidth: 'none' }}>
        <div className={s.modalTitle}>📡 Importação pelo DF-e — Notas emitidas contra a organização</div>
        <div className={s.modalBody}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={() => sincronizar()}
              disabled={sincronizando || consultaBloqueada}
              title={consultaBloqueada
                ? `Disponível em ${minutosFaltando} min — a SEFAZ exige 1h entre consultas (o sistema sincroniza sozinho a cada 70 min)`
                : 'Consulta a SEFAZ a partir do último NSU'}>
              {sincronizando ? 'Consultando a SEFAZ...' : '🔄 Sincronizar com a SEFAZ'}
            </button>
            <button className={s.btnErp} onClick={reconsultarTudo} disabled={sincronizando || consultaBloqueada}
              title={consultaBloqueada
                ? `Disponível em ${minutosFaltando} min — a SEFAZ exige 1h entre consultas`
                : 'Zera o NSU e busca de novo todos os documentos dos últimos 90 dias — use se estiver faltando alguma nota'}>
              ↺ Reconsultar tudo
            </button>
            <input className={s.inputErp} style={{ flex: 1, minWidth: 220 }}
              placeholder="Filtrar por emitente, CNPJ ou chave..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && carregar()} />
            <span style={{ fontSize: 11, color: '#666' }}>
              {controle?.ultima_consulta
                ? `Última consulta: ${fmtDT(controle.ultima_consulta)} · NSU ${controle.ult_nsu}/${controle.max_nsu || '—'}`
                : 'Nenhuma consulta realizada ainda'}
              {consultaBloqueada && (
                <span style={{ color: '#b45309' }}> · nova consulta em {minutosFaltando} min</span>
              )}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
              Período (emissão) — de
              <InputDataFiltro className={s.inputErp} value={zipDe} onChange={e => setZipDe(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && filtrarPeriodo()} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
              até
              <InputDataFiltro className={s.inputErp} value={zipAte} onChange={e => setZipAte(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && filtrarPeriodo()} />
            </label>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={filtrarPeriodo} disabled={carregando}
              title="Mostra na lista as notas emitidas no período (inclusive meses anteriores)">
              🔍 Filtrar
            </button>
            <button className={s.btnErp} onClick={baixarZip} disabled={baixandoZip}
              title="Baixa em um único .zip os XMLs completos das notas do período (DF-e + entradas importadas)">
              {baixandoZip ? 'Compactando…' : '🗜️ Baixar XMLs (.zip)'}
            </button>
            <input className={s.inputErp} style={{ flex: 1, minWidth: 300 }}
              placeholder="Nota não apareceu? Cole a chave de acesso (44 dígitos)..."
              value={chaveConsulta}
              onChange={e => setChaveConsulta(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && consultarChave()} />
            <button className={s.btnErp} onClick={consultarChave} disabled={consultandoChave}
              title="Diagnóstico da nota: verifica a base local, o Ambiente Nacional (baixa na hora se disponível) e a situação na SEFAZ da UF emitente">
              {consultandoChave ? 'Consultando…' : '🔎 Consultar chave'}
            </button>
          </div>

          {erro  && <div className={s.erroMsg}>{erro}</div>}
          {aviso && <div className={s.sucessoMsg}>{aviso}</div>}

          <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>
            {periodo
              ? <>Mostrando notas emitidas de <strong>{fmtD(periodo.de)}</strong> a <strong>{fmtD(periodo.ate)}</strong>
                  {' · '}<button type="button" onClick={limparPeriodo}
                    style={{ background: 'none', border: 'none', padding: 0, color: '#1565FF', cursor: 'pointer', fontSize: 11 }}>
                    voltar aos últimos {janelaDias || 30} dias
                  </button></>
              : <>Mostrando notas dos últimos <strong>{janelaDias || 30} dias</strong> — para meses anteriores, informe o período e clique em Filtrar</>}
          </div>

          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table className={s.modalTabela}>
              <thead><tr>
                <th style={{ width: 110 }}>Data Emissão</th>
                <th>Emitente</th>
                <th style={{ width: 130 }}>CNPJ</th>
                <th style={{ width: 100, textAlign: 'right' }}>Valor</th>
                <th style={{ width: 110 }}>Consultado em</th>
                <th style={{ width: 105 }}>Status</th>
                <th style={{ width: 270 }}>Ações</th>
              </tr></thead>
              <tbody>
                {carregando && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 14 }}>Carregando...</td></tr>}
                {!carregando && docs.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 14 }}>
                    Nenhum documento — clique em "Sincronizar com a SEFAZ" para buscar as notas emitidas contra o CNPJ da organização.
                  </td></tr>
                )}
                {docs.map(d => {
                  const sitCancel = SITUACAO[d.situacao];
                  return (
                    <tr key={d.id} title={d.chave_acesso ? `Chave: ${d.chave_acesso}` : ''}>
                      <td style={{ fontSize: 11 }}>{fmtDT(d.data_emissao)}</td>
                      <td>{d.nome_emitente || '—'}</td>
                      <td style={{ fontSize: 11 }}>{fmtCnpj(d.cnpj_emitente)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoeda(d.valor)}</td>
                      <td style={{ fontSize: 11 }} title="Quando este registro foi obtido da SEFAZ">{fmtDT(d.criado_em)}</td>
                      <td>
                        {sitCancel
                          ? <span className={`${s.badge} ${s.badgeCancelado}`}>{sitCancel}</span>
                          : d.manifestacao === 'nao_realizada'
                            ? <span className={s.badge} style={{ background: '#fee2e2', color: '#b91c1c' }}>Não realizada</span>
                            : d.importada
                              ? <span className={`${s.badge} ${s.badgeAprovado}`}>Importada{d.manifestacao === 'confirmada' ? ' ✓' : ''}</span>
                              : d.tipo === 'nfe'
                                ? <span className={`${s.badge} ${s.badgeAberto}`}>XML completo</span>
                                : d.manifestada
                                  ? <span className={s.badge} style={{ background: '#fef3c7', color: '#92400e' }}>Aguard. XML</span>
                                  : <span className={s.badge} style={{ background: '#e5e7eb', color: '#374151' }}>Resumo</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {!sitCancel && d.tipo === 'nfe' && !d.importada && (
                          <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                            style={{ height: 22, fontSize: 11, padding: '0 8px' }}
                            disabled={ocupadoId === d.id}
                            onClick={() => importar(d)}>
                            {ocupadoId === d.id ? '...' : '📥 Importar'}
                          </button>
                        )}
                        {!sitCancel && d.tipo === 'resumo' && !d.manifestada && (
                          <button className={s.btnErp}
                            style={{ height: 22, fontSize: 11, padding: '0 8px' }}
                            disabled={ocupadoId === d.id}
                            title="Registra a Ciência da Operação na SEFAZ para liberar o XML completo"
                            onClick={() => manifestar(d)}>
                            {ocupadoId === d.id ? '...' : '✋ Manifestar ciência'}
                          </button>
                        )}
                        {!sitCancel && d.tipo === 'resumo' && d.manifestada && (
                          <span style={{ fontSize: 11, color: '#92400e' }}>Sincronize novamente</span>
                        )}
                        {d.importada && <span style={{ fontSize: 11, color: '#166534' }}>✓ Entrada lançada</span>}
                        {!sitCancel && !d.importada && d.manifestacao !== 'nao_realizada' && (
                          <button className={s.btnErp}
                            style={{ height: 22, fontSize: 11, padding: '0 6px', marginLeft: 4, color: '#b91c1c' }}
                            disabled={ocupadoId === d.id}
                            title="Manifestar Operação Não Realizada (210240) — negócio não se concretizou"
                            onClick={() => naoRealizada(d)}>
                            ⊘ Não realizada
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
            💡 O sistema consulta a SEFAZ automaticamente a cada <strong>70 minutos</strong>, registrando a{' '}
            <strong>Ciência da Operação</strong> nos resumos (o XML completo chega no ciclo seguinte).
            Ao <strong>importar</strong> uma nota, a <strong>Confirmação da Operação</strong> é enviada
            automaticamente; use <strong>Não realizada</strong> quando o negócio não se concretizou.
            A consulta usa o certificado digital A1 da organização e segue o controle de NSU exigido pela SEFAZ.
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}
